import type { HookAPI } from 'antd/es/modal/useModal'
import { useState } from 'react'

import { priceSegmentsApi } from '@/api/search/priceSegmentsApi'
import { useFeedPriceSegmentsClientKey } from '@/hooks/search/useFeedPriceSegments'
import { useFeedStore } from '@/stores/feed/feedStore'
import {
  usePriceSegmentOptions,
  usePriceSegmentsStore,
} from '@/stores/feed/priceSegmentsStore'
import type { PriceSegmentOption } from '@/types'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import {
  applySegmentPrices,
  collectSegmentPriceOffers,
} from '@/utils/search/segmentPrices/segmentPrices'

export const usePriceSegmentsPicker = (modal: HookAPI) => {
  const clientKey = useFeedPriceSegmentsClientKey()
  const options = usePriceSegmentOptions(clientKey)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const loadOptions = async () => {
    if (options || loading) return
    setLoading(true)
    setLoadError(false)
    try {
      const response = await priceSegmentsApi.getPriceSegments(
        clientKey || undefined
      )
      usePriceSegmentsStore.getState().setOptions(clientKey, response)
    } catch (error) {
      setLoadError(true)
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }

  const loadSegmentPrices = async (selected: PriceSegmentOption[]) => {
    const { products, setProducts } = useFeedStore.getState()
    const offers = collectSegmentPriceOffers(products)
    if (selected.length === 0 || offers.length === 0) return
    const { setSegmentPricesLoading } = usePriceSegmentsStore.getState()
    setSegmentPricesLoading(true)
    try {
      const response = await priceSegmentsApi.getSegmentPrices({
        client: clientKey || undefined,
        segmentCodes: selected.map(({ code }) => code),
        offers,
      })
      setProducts(
        applySegmentPrices(useFeedStore.getState().products, response)
      )
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setSegmentPricesLoading(false)
    }
  }

  const show = (selected: PriceSegmentOption[]) => {
    usePriceSegmentsStore.getState().setSelected(clientKey, selected)
    void loadSegmentPrices(selected)
  }

  const reset = () =>
    usePriceSegmentsStore.getState().setSelected(clientKey, [])

  return {
    clientKey,
    options,
    loading,
    loadError,
    loadOptions,
    show,
    reset,
  }
}
